import type { FunctionComponent } from 'react';
import { useState } from 'react';
import type { Column, ComponentPropsGetterR, SortingRule } from 'react-table';
import ReactTable from 'react-table';
import store from 'store';
import * as _ from 'lodash';
import COLORS from '../../theme/colors';

export const MINIMUM_PAGE_SIZE = 20;

const getTdProps = () => ({
  style: {
    border: 'none',
    height: '44px',
    paddingLeft: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#35465e',
  },
});

type SimpleReactTableProps = {
  data: any[];
  columns: Column[];
  defaultSortMode: SortingRule[];
  onSortedChanged?(sortMode: SortingRule): void;
  showPagination?: boolean;
  cacheId?: string; // provided string to save certain values on the table to browser cache
  getTrProps?: ComponentPropsGetterR;
  minRows?: number;
  getTrGroupProps?: ComponentPropsGetterR;
  isSortable?: boolean;
};

// right now, we only are caching pageSize, but this can eventually be extended to other fields as well
const getStorageIdForField = (cacheId: string, field: 'pageSize') => `${cacheId}_${field}`;

const noopGetter = () => ({});

/**
 * SimpleReactTable is a scaffold for a straight-forward React table component.
 * If a `pageSizeIdToSave` prop is provided, then we will save the page size
 * selected by the user to the local browser cache.
 */
const SimpleReactTable: FunctionComponent<SimpleReactTableProps> = ({
  isSortable = true, // Default to true if not provided
  ...props
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    if (props.cacheId) {
      return Math.max(
        store.get(getStorageIdForField(props.cacheId, 'pageSize')) || MINIMUM_PAGE_SIZE,
        MINIMUM_PAGE_SIZE,
      );
    }

    return MINIMUM_PAGE_SIZE;
  });

  const onPageSizeChange = (newPageSize: number, newPage: number) => {
    setPageSize(newPageSize);
    setPage(newPage);

    // store in browser cache if pageSizeIdToSave provided
    if (props.cacheId) {
      store.set(getStorageIdForField(props.cacheId, 'pageSize'), newPageSize);
    }
  };

  // Apply styles based on attributes
  const styledColumns = props.columns.map((column) => {
    // resizable is true by default
    if (column.resizable || _.isNil(column.resizable)) {
      return {
        ...column,
        style: {
          ...column.style,
          borderRight: `1px solid ${COLORS.fossaGray10}`,
        },
      };
    }
    return column;
  });

  const onSortedChanged = props.onSortedChanged ? props.onSortedChanged : () => {};

  return (
    <ReactTable
      className="simple-react-table"
      data={props.data}
      columns={styledColumns}
      minRows={props.minRows ?? 5}
      collapseOnDataChange={false}
      collapseOnSortingChange={false}
      onSortedChange={newSortMode => onSortedChanged(newSortMode[0] as SortingRule)}
      defaultSorted={props.defaultSortMode}
      showPagination={props.showPagination || false}
      pageSizeOptions={[MINIMUM_PAGE_SIZE, 50, 100]}
      pageSize={pageSize}
      page={page}
      onPageSizeChange={onPageSizeChange}
      onPageChange={setPage}
      getTdProps={getTdProps}
      getTrProps={(props.getTrProps || noopGetter) as ComponentPropsGetterR}
      getTrGroupProps={(props.getTrGroupProps || noopGetter) as ComponentPropsGetterR}
      sortable={isSortable}
    />
  );
};

export default SimpleReactTable;
